// ブラウザ完結版の同期仮想ファイルシステム（VFS）。
// 既存 lib/*.mjs は同期 fs（readFileSync 等）で書かれており、そのまま流用するため
// 「op 前に必要ファイルを常駐ハイドレート → 同期コードで読み書き（間に await 混在可）→ op 後に flush」
// という単一シングルトン方式を採る（backend が async mutex で全 mutating op を直列化する）。
//
// 3 層:
//   seed : 起動時に 1 度だけ載せる読み取り専用ベース（バンドル config/data/schemas + _runtime 既定 JSON）
//   layer: op ごとにハイドレート/書き込みされる可変層（値 or null=削除墓標）
//   explicitDirs: 空ディレクトリの mkdir を覚えるための集合
// キーは正規化済み絶対 posix パス（末尾スラッシュなし。ルートのみ '/'）。
import { normalize } from './shims/path.mjs';

function norm(p) {
    let n = normalize(String(p));
    if (n.length > 1 && n.endsWith('/')) n = n.slice(0, -1);
    return n;
}

class Vfs {
    constructor() {
        this.seed = new Map();          // path -> string|Uint8Array
        this.layer = new Map();         // path -> string|Uint8Array|null(tombstone)
        this.explicitDirs = new Set();  // path
        this.dirty = new Set();         // 書き込まれた path（flush 対象）
        this.unlinked = new Set();      // 削除された path（IDB からも消す）
    }

    // ── seed（起動時のみ） ─────────────────────────────
    seedFile(p, data) {
        this.seed.set(norm(p), data);
    }

    // ── layer 操作（op ごと） ─────────────────────────
    /** ハイドレート: 既存内容として layer に載せる（dirty にしない）。 */
    hydrate(p, data) {
        const k = norm(p);
        this.layer.set(k, data);
        this._touchDirs(k);
    }

    /** op 終了後: layer / dirty / unlinked をクリアして次 op をクリーンにする（seed は残す）。 */
    resetLayer() {
        this.layer.clear();
        this.explicitDirs.clear();
        this.dirty.clear();
        this.unlinked.clear();
    }

    _touchDirs(k) {
        let d = k.slice(0, k.lastIndexOf('/'));
        while (d && d.length > 1) {
            this.explicitDirs.add(d);
            d = d.slice(0, d.lastIndexOf('/'));
        }
    }

    // ── node:fs 相当 ─────────────────────────────────
    _resolve(p) {
        const k = norm(p);
        if (this.layer.has(k)) {
            const v = this.layer.get(k);
            return v === null ? undefined : v; // 墓標は「無い」
        }
        if (this.seed.has(k)) return this.seed.get(k);
        return undefined;
    }

    existsFile(p) {
        return this._resolve(p) !== undefined;
    }

    isDirectory(p) {
        const k = norm(p);
        if (this.explicitDirs.has(k)) return true;
        const prefix = k + '/';
        for (const [key, v] of this.layer) if (v !== null && key.startsWith(prefix)) return true;
        for (const key of this.seed.keys()) {
            if (!key.startsWith(prefix)) continue;
            if (this.layer.get(key) === null) continue; // seed を覆う墓標
            return true;
        }
        return false;
    }

    exists(p) {
        return this.existsFile(p) || this.isDirectory(p);
    }

    read(p) {
        const v = this._resolve(p);
        if (v === undefined) {
            const err = new Error(`ENOENT: no such file: ${norm(p)}`);
            err.code = 'ENOENT';
            throw err;
        }
        return v;
    }

    write(p, data) {
        const k = norm(p);
        this.layer.set(k, data);
        this.dirty.add(k);
        this.unlinked.delete(k);
        this._touchDirs(k);
    }

    mkdir(p) {
        const k = norm(p);
        this.explicitDirs.add(k);
        this._touchDirs(k);
    }

    /** 直下の子エントリ名（重複排除）。withFileTypes=true なら Dirent 風。 */
    readdir(p, withFileTypes = false) {
        const k = norm(p);
        const prefix = k === '/' ? '/' : k + '/';
        const children = new Map(); // name -> isDir
        const consider = (key, isTombstone) => {
            if (!key.startsWith(prefix)) return;
            const rest = key.slice(prefix.length);
            if (rest.length === 0) return;
            const slash = rest.indexOf('/');
            const name = slash === -1 ? rest : rest.slice(0, slash);
            const isDir = slash !== -1;
            if (isTombstone) {
                // 直接の子ファイルが墓標なら候補から外す（サブディレクトリ配下の墓標は無視）
                if (!isDir && children.get(name) === false) children.delete(name);
                return;
            }
            if (!children.has(name) || isDir) children.set(name, children.get(name) || isDir);
        };
        for (const key of this.seed.keys()) {
            if (this.layer.get(key) === null) continue;
            consider(key, false);
        }
        for (const [key, v] of this.layer) consider(key, v === null);
        for (const d of this.explicitDirs) {
            if (!d.startsWith(prefix)) continue;
            const rest = d.slice(prefix.length);
            const name = rest.indexOf('/') === -1 ? rest : rest.slice(0, rest.indexOf('/'));
            if (name && !children.has(name)) children.set(name, true);
        }
        const names = [...children.keys()];
        if (!withFileTypes) return names;
        return names.map((name) => ({
            name,
            isDirectory: () => children.get(name) === true,
            isFile: () => children.get(name) === false,
            isSymbolicLink: () => false,
        }));
    }

    rm(p, recursive = false) {
        const k = norm(p);
        const targets = [];
        // ファイル自身
        if (this.existsFile(k)) targets.push(k);
        // 配下（recursive）
        if (recursive) {
            const prefix = k + '/';
            for (const [key, v] of this.layer) if (v !== null && key.startsWith(prefix)) targets.push(key);
            for (const key of this.seed.keys()) if (key.startsWith(prefix) && this.layer.get(key) !== null) targets.push(key);
        }
        for (const t of new Set(targets)) {
            if (this.seed.has(t)) this.layer.set(t, null); // seed を覆い隠す墓標
            else this.layer.delete(t);
            this.dirty.delete(t);
            this.unlinked.add(t);
        }
        // explicitDirs 掃除
        for (const d of [...this.explicitDirs]) {
            if (d === k || d.startsWith(k + '/')) this.explicitDirs.delete(d);
        }
    }

    // ── flush 支援 ───────────────────────────────────
    /** dirty のうち prefix 配下を [path, data] で返す。 */
    dirtyUnder(prefix) {
        const out = [];
        for (const key of this.dirty) {
            if (key === prefix || key.startsWith(prefix.endsWith('/') ? prefix : prefix + '/')) {
                const v = this.layer.get(key);
                if (v !== undefined && v !== null) out.push([key, v]);
            }
        }
        return out;
    }

    /** unlinked のうち prefix 配下の path 配列。 */
    unlinkedUnder(prefix) {
        const pfx = prefix.endsWith('/') ? prefix : prefix + '/';
        return [...this.unlinked].filter((key) => key === prefix || key.startsWith(pfx));
    }

    /** IndexedDB へ書き終えたキーを差分追跡から外す。
     *  ユニット境界の checkpoint flush を増分に保つ（外さないと以降の checkpoint が
     *  過去分すべてを再書き込みし、書込量が O(N²) に膨らむ）。
     *  [path, data] を受けた場合、flush 中に同じパスが更新されていれば新しい dirty を残す。 */
    markFlushed(dirtyEntries, unlinkedKeys) {
        for (const entry of dirtyEntries) {
            if (Array.isArray(entry)) {
                const [key, flushedValue] = entry;
                if (this.layer.get(key) === flushedValue) this.dirty.delete(key);
            } else {
                // 旧呼び出し形式（path のみ）も互換維持する。
                this.dirty.delete(entry);
            }
        }
        for (const k of unlinkedKeys) this.unlinked.delete(k);
    }
}

export const vfs = new Vfs();
